import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { previewImport, commitImport } from "../../services/import.service";
import { getApiErrorMessage } from "../../lib/apiClient";
import type { ImportResultSummary, ParsedImportRow } from "../../types";

const PLACEHOLDER = `Parent Name,Phone,Student Name,Student IC,Age
Ahmad Ali,0123456789,Ali Ahmad,160101-10-1234,10
Ahmad Ali,0123456789,Aisyah Ahmad,180505-10-5678,8`;

export function CsvImportPanel({ eventId }: { eventId: string }): JSX.Element {
  const queryClient = useQueryClient();
  const [csvText, setCsvText] = useState("");
  const [rows, setRows] = useState<ParsedImportRow[] | null>(null);
  const [selected, setSelected] = useState<Record<number, boolean>>({});
  const [result, setResult] = useState<ImportResultSummary | null>(null);

  const previewMutation = useMutation({
    mutationFn: () => previewImport(eventId, csvText),
    onSuccess: (data) => {
      setRows(data.rows);
      setResult(null);
      const initialSelection: Record<number, boolean> = {};
      data.rows.forEach((r) => {
        initialSelection[r.rowNumber] = r.isValid;
      });
      setSelected(initialSelection);
    },
    onError: (err) => toast.error(getApiErrorMessage(err)),
  });

  const commitMutation = useMutation({
    mutationFn: (selectedRows: ParsedImportRow[]) => commitImport(eventId, selectedRows),
    onSuccess: (summary) => {
      setResult(summary);
      toast.success("Import complete");
      queryClient.invalidateQueries({ queryKey: ["events", eventId, "enrolments"] });
      queryClient.invalidateQueries({ queryKey: ["events", eventId, "dashboard"] });
    },
    onError: (err) => toast.error(getApiErrorMessage(err)),
  });

  const selectedCount = useMemo(() => Object.values(selected).filter(Boolean).length, [selected]);

  const handlePreview = (): void => {
    if (!csvText.trim()) {
      toast.error("Paste some CSV data first");
      return;
    }
    previewMutation.mutate();
  };

  const handleSelectAll = (): void => {
    if (!rows) return;
    const next: Record<number, boolean> = {};
    rows.forEach((r) => (next[r.rowNumber] = r.isValid));
    setSelected(next);
  };

  const handleClearAll = (): void => {
    if (!rows) return;
    const next: Record<number, boolean> = {};
    rows.forEach((r) => (next[r.rowNumber] = false));
    setSelected(next);
  };

  const handleImport = (): void => {
    if (!rows) return;
    const toImport = rows.filter((r) => selected[r.rowNumber] && r.isValid);
    if (toImport.length === 0) {
      toast.error("Select at least one valid row");
      return;
    }
    commitMutation.mutate(toImport);
  };

  const handleReset = (): void => {
    setCsvText("");
    setRows(null);
    setSelected({});
    setResult(null);
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="label" htmlFor="csv-text">
          Paste CSV data
        </label>
        <p className="mb-2 text-xs text-slate-500 dark:text-slate-400">
          Format: <code className="rounded bg-slate-100 px-1 dark:bg-slate-700">Parent Name,Phone,Student Name,Student IC,Age</code> (header row optional, one
          student per line)
        </p>
        <textarea
          id="csv-text"
          className="input font-mono"
          rows={6}
          placeholder={PLACEHOLDER}
          value={csvText}
          onChange={(e) => setCsvText(e.target.value)}
        />
        <div className="mt-2 flex gap-2">
          <button type="button" className="btn-primary" onClick={handlePreview} disabled={previewMutation.isPending}>
            {previewMutation.isPending ? "Parsing..." : "Preview"}
          </button>
          {rows && (
            <button type="button" className="btn-secondary" onClick={handleReset}>
              Start Over
            </button>
          )}
        </div>
      </div>

      {rows && rows.length > 0 && (
        <div className="animate-fade-in">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              {rows.filter((r) => r.isValid).length} valid, {rows.filter((r) => !r.isValid).length} invalid of {rows.length} rows -{" "}
              {selectedCount} selected
            </p>
            <div className="flex gap-2">
              <button type="button" className="text-sm font-medium text-brand-600 hover:text-brand-800 dark:text-brand-400 dark:hover:text-brand-300 transition-colors duration-150" onClick={handleSelectAll}>
                Select All Valid
              </button>
              <button type="button" className="text-sm font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors duration-150" onClick={handleClearAll}>
                Clear All
              </button>
            </div>
          </div>

          <div className="card max-h-96 overflow-auto">
            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700 text-sm">
              <thead className="sticky top-0 bg-slate-50 dark:bg-slate-700/60">
                <tr>
                  <th className="px-3 py-2 text-left">
                    <span className="sr-only">Select</span>
                  </th>
                  <th className="px-3 py-2 text-left font-medium uppercase text-xs text-slate-500 dark:text-slate-400">Parent</th>
                  <th className="px-3 py-2 text-left font-medium uppercase text-xs text-slate-500 dark:text-slate-400">Phone</th>
                  <th className="px-3 py-2 text-left font-medium uppercase text-xs text-slate-500 dark:text-slate-400">Student</th>
                  <th className="px-3 py-2 text-left font-medium uppercase text-xs text-slate-500 dark:text-slate-400">IC</th>
                  <th className="px-3 py-2 text-left font-medium uppercase text-xs text-slate-500 dark:text-slate-400">Age</th>
                  <th className="px-3 py-2 text-left font-medium uppercase text-xs text-slate-500 dark:text-slate-400">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {rows.map((row) => (
                  <tr
                    key={row.rowNumber}
                    className={
                      "transition-colors duration-100 " +
                      (row.isValid
                        ? "hover:bg-slate-100 dark:hover:bg-slate-700/60"
                        : "bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/30")
                    }
                  >
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        disabled={!row.isValid}
                        checked={!!selected[row.rowNumber]}
                        onChange={(e) => setSelected({ ...selected, [row.rowNumber]: e.target.checked })}
                      />
                    </td>
                    <td className="px-3 py-2 dark:text-slate-200">
                      {row.parentName}
                      {row.existingParent && <span className="ml-1 text-xs text-slate-400 dark:text-slate-500">(existing)</span>}
                    </td>
                    <td className="px-3 py-2 dark:text-slate-200">{row.phone}</td>
                    <td className="px-3 py-2 dark:text-slate-200">
                      {row.studentName}
                      {row.existingStudent && <span className="ml-1 text-xs text-slate-400 dark:text-slate-500">(existing)</span>}
                    </td>
                    <td className="px-3 py-2 dark:text-slate-200">{row.studentIc}</td>
                    <td className="px-3 py-2 dark:text-slate-200">{row.age}</td>
                    <td className="px-3 py-2">
                      {row.isValid ? (
                        <span className="text-green-700 dark:text-green-400">Valid</span>
                      ) : (
                        <span className="text-red-700 dark:text-red-400">{row.errors.join("; ")}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button
            type="button"
            className="btn-primary mt-3"
            onClick={handleImport}
            disabled={commitMutation.isPending || selectedCount === 0}
          >
            {commitMutation.isPending ? "Importing..." : `Import Selected (${selectedCount})`}
          </button>
        </div>
      )}

      {result && (
        <div className="card border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/40 animate-fade-in">
          <p className="font-medium text-green-800 dark:text-green-300">Import summary</p>
          <ul className="mt-2 grid grid-cols-2 gap-1 text-sm text-green-700 sm:grid-cols-3 dark:text-green-300">
            <li>Parents created: {result.parentsCreated}</li>
            <li>Parents existing: {result.parentsExisting}</li>
            <li>Students created: {result.studentsCreated}</li>
            <li>Students existing: {result.studentsExisting}</li>
            <li>Enrolments created: {result.enrolmentsCreated}</li>
            <li>Enrolments skipped: {result.enrolmentsSkipped}</li>
          </ul>
        </div>
      )}
    </div>
  );
}
